import { useState } from 'react';
import { Check, ChevronDown, ChevronRight, Copy } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface JsonPreProps {
  /** Either an object to stringify or a pre-formatted string. */
  data: unknown;
  /** Optional summary label shown in the disclosure trigger. */
  label?: string;
  /** Open by default. */
  defaultOpen?: boolean;
  className?: string;
}

/**
 * Collapsible "show raw" panel that pretty-prints JSON with a copy button.
 * Designed for the brief / audit detail "raw" footer.
 */
export function JsonPre({
  data,
  label = 'Show raw JSON',
  defaultOpen = false,
  className,
}: JsonPreProps): JSX.Element {
  const [open, setOpen] = useState(defaultOpen);
  const [copied, setCopied] = useState(false);

  const formatted =
    typeof data === 'string' ? data : JSON.stringify(data, null, 2);

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(formatted);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable; silent */
    }
  };

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-border/70 bg-card/40 backdrop-blur',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
          aria-expanded={open}
        >
          {open ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
          {label}
        </button>
        {open ? (
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-background/60 px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            aria-label="Copy JSON"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3" /> Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" /> Copy
              </>
            )}
          </button>
        ) : null}
      </div>
      {open ? (
        <pre className="max-h-[480px] overflow-auto border-t border-border/70 bg-background/60 p-4 font-mono text-[11px] leading-relaxed text-muted-foreground">
          {formatted}
        </pre>
      ) : null}
    </div>
  );
}
